let PROMPT_LOG = [];

const normalizeFreeLLMModelCode = (model) => {
    const raw = String(model || '').trim();
    if (!raw) return 'auto';
    return raw.startsWith('freellmapi:') ? raw.substring('freellmapi:'.length) : raw;
};

const DEFAULT_AI_MODELS = [
    { key: 'gemini:gemini-1.5-flash', name: 'Gemini 1.5 Flash', provider: 'gemini', endpoint: 'default', modelCode: 'gemini-1.5-flash', source: 'builtin', enabled: true },
    { key: 'gemini:gemini-1.5-pro', name: 'Gemini 1.5 Pro', provider: 'gemini', endpoint: 'default', modelCode: 'gemini-1.5-pro', source: 'builtin', enabled: true },
    { key: 'freellmapi:gemini-3.1-pro-preview', name: 'Gemini 3.1 Pro Preview', provider: 'freellmapi', endpoint: 'https://freellm.gofunwith.com/v1', modelCode: 'gemini-3.1-pro-preview', source: 'builtin', enabled: true },
    { key: 'freellmapi:llama-3.1-8b-instant', name: 'Llama 3.1 8B Instant', provider: 'freellmapi', endpoint: 'https://freellm.gofunwith.com/v1', modelCode: 'llama-3.1-8b-instant', source: 'builtin', enabled: true }
];

const getUsableModels = (adminState = {}) => {
    return Array.isArray(adminState.aiModels) && adminState.aiModels.length > 0
        ? adminState.aiModels
        : DEFAULT_AI_MODELS;
};

const createOperationalAIError = (message, metadata = {}) => {
    const err = new Error(message);
    Object.assign(err, metadata);
    err.isOperational = true;
    return err;
};

const extractChatCompletionContent = (data = {}, fallbackStatus = 'unknown') => {
    const content = data.choices?.[0]?.message?.content;
    if (typeof content !== 'string' || content.trim() === '') {
        throw createOperationalAIError('AI provider returned an empty assistant response.', {
            code: 'FREELLM_EMPTY_RESPONSE',
            requestId: data.requestId,
            upstreamStatus: data.upstreamStatus || fallbackStatus
        });
    }
    return content;
};

export const aiService = {
    resolveAIExecutionConfig(adminState) {
        const provider = adminState.aiProvider || 'gemini';
        const mode = adminState.routingMode || 'fixed';
        const aiModels = getUsableModels(adminState);

        if (provider === 'freellmapi' && mode === 'auto') {
            return {
                provider: 'freellmapi',
                requestedModel: 'auto',
                endpoint: adminState.freellmUrl || 'https://freellm.gofunwith.com/v1',
                apiKey: adminState.freellmApiKey || ''
            };
        }

        const activeKey = adminState.activeModelKey;
        if (!activeKey) {
            const firstModel = aiModels.find(m => m.provider === provider && m.enabled);
            if (!firstModel) {
                throw new Error(`[AI Configuration Error] 활성화된 ${provider} 모델이 존재하지 않습니다.`);
            }
            return {
                provider,
                requestedModel: firstModel.modelCode,
                endpoint: firstModel.endpoint,
                apiKey: this.getApiKeyForProvider(provider, adminState)
            };
        }

        let model = aiModels.find(m => m.key === activeKey);
        if (!model) {
            model = aiModels.find(m => m.provider === provider && m.enabled);
            if (!model) {
                throw new Error(`[AI Configuration Error] 지정된 활성 모델(${activeKey})을 카탈로그에서 찾을 수 없고 대체 가능한 ${provider} 모델도 없습니다.`);
            }
            console.warn(`[AI Configuration Recovery] 활성 모델(${activeKey})을 찾지 못해 ${model.key}로 대체합니다.`);
        }

        if (model.unavailable) {
            throw new Error(`[AI Configuration Error] 지정된 모델(${model.name})이 사용 불가 상태(unavailable)입니다. 연결 테스트를 통해 상태를 재확인하십시오.`);
        }

        if (model.verificationExpiresAt) {
            const expires = new Date(model.verificationExpiresAt).getTime();
            if (Date.now() > expires) {
                throw new Error(`[AI Configuration Error] 지정된 모델(${model.name})의 검증 기간(TTL 24H)이 만료되었습니다. 연결 테스트를 통해 상태를 재검증하십시오.`);
            }
        }

        if (!model.enabled) {
            throw new Error(`[AI Configuration Error] 지정된 모델(${model.name})이 비활성화 상태입니다. 모델 카탈로그에서 먼저 활성화하십시오.`);
        }

        if (model.provider !== provider) {
            throw new Error(`[AI Configuration Error] 현재 지정된 모델 제공자(${model.provider})가 활성 AI 제공자(${provider})와 일치하지 않습니다.`);
        }

        return {
            provider,
            requestedModel: provider === 'freellmapi' ? normalizeFreeLLMModelCode(model.modelCode) : model.modelCode,
            endpoint: model.endpoint,
            apiKey: this.getApiKeyForProvider(provider, adminState)
        };
    },

    getApiKeyForProvider(provider, adminState) {
        if (provider === 'gemini') return adminState.geminiApiKey || '';
        if (provider === 'openrouter') return adminState.openrouterApiKey || '';
        if (provider === 'freellmapi') return adminState.freellmApiKey || '';
        return '';
    },

    async callAI(prompt, systemPrompt = "", labelId = "unnamed_call") {
        const adminState = JSON.parse(localStorage.getItem('auraDoc_adminState') || '{}');
        
        let resolvedSystemPrompt = systemPrompt;
        if ((!resolvedSystemPrompt || resolvedSystemPrompt.trim() === '') && labelId && adminState.prompts) {
            if (labelId.startsWith('PROMPT_PROPOSAL_SLIDE_RENDER')) {
                resolvedSystemPrompt = adminState.prompts.PROMPT_PROPOSAL_SLIDE;
            } else if (labelId.startsWith('PROMPT_QUICKDOC_GEN')) {
                resolvedSystemPrompt = adminState.prompts.PROMPT_QUICKDOC_GEN;
            } else if (adminState.prompts[labelId]) {
                resolvedSystemPrompt = adminState.prompts[labelId];
            }
        }

        const globalInstruction = "반드시 마크다운 수식 기호($...$)나 LaTeX 기호(\\rightarrow 등)를 사용하지 마라. 화살표는 '->', 체크박스는 '[ ]' 또는 '[x]' 등 일반 텍스트와 유니코드 이모지만 사용하라.";
        const finalSystem = resolvedSystemPrompt ? `${resolvedSystemPrompt}\n\n${globalInstruction}` : globalInstruction;
        
        const execConfig = this.resolveAIExecutionConfig(adminState);
        const provider = execConfig.provider;
        const requestedModel = execConfig.requestedModel;

        let logs = [];
        try { logs = JSON.parse(localStorage.getItem('auraDoc_aiLogs') || '[]'); } catch(e) {}

        const logEntry = { 
            timestamp: new Date().toISOString(), 
            labelId, 
            provider,
            model: requestedModel, // target model requested
            resolvedModel: requestedModel, // placeholder, updated on response
            prompt, 
            system: finalSystem 
        };
        logs.push(logEntry);
        if (logs.length > 100) logs.shift();
        localStorage.setItem('auraDoc_aiLogs', JSON.stringify(logs));
        console.log(`🤖 AI CALL [${labelId}] via ${provider}/${requestedModel}:`, logEntry);

        window.admin?.setSystemStatus('busy');

        const startTime = performance.now();
        let responseObj;
        try {
            if (provider === 'local') {
                responseObj = await this.callOllama(prompt, execConfig.endpoint, requestedModel);
            } else if (provider === 'openrouter') {
                responseObj = await this.callOpenRouter(prompt, finalSystem, requestedModel, execConfig.apiKey);
            } else if (provider === 'freellmapi') {
                responseObj = await this.callFreeLLM(prompt, finalSystem, requestedModel, execConfig.endpoint, execConfig.apiKey);
            } else {
                responseObj = await this.callProxy(prompt, finalSystem, requestedModel, execConfig.apiKey);
            }
            
            const duration = ((performance.now() - startTime) / 1000).toFixed(2);
            let result = responseObj.content;
            const actualResolved = responseObj.resolvedModel;

            if (result) {
                result = result
                    .replace(/\$\\rightarrow\$/g, '->')
                    .replace(/\\rightarrow/g, '->')
                    .replace(/\$/g, '') 
                    .replace(/\\times/g, 'x')
                    .replace(/\\cdot/g, '·');
            }

            try {
                let logs = JSON.parse(localStorage.getItem('auraDoc_aiLogs') || '[]');
                const lastLog = logs[logs.length - 1];
                if (lastLog && lastLog.labelId === labelId) {
                    lastLog.status = 'SUCCESS';
                    lastLog.duration = `${duration}s`;
                    lastLog.resolvedModel = actualResolved;
                    localStorage.setItem('auraDoc_aiLogs', JSON.stringify(logs));
                }
            } catch (e) {}

            window.admin?.setSystemStatus('ready');
            return result;
        } catch (error) {
            const duration = ((performance.now() - startTime) / 1000).toFixed(2);
            window.admin?.setSystemStatus('error');
            if (labelId === 'connection-test' && error.isOperational === true) {
                console.warn(`AI Call [${labelId}] failed:`, error.message);
            } else {
                console.error(`AI Call [${labelId}] failed:`, error);
            }
            
            try {
                let logs = JSON.parse(localStorage.getItem('auraDoc_aiLogs') || '[]');
                const lastLog = logs[logs.length - 1];
                if (lastLog && lastLog.labelId === labelId) {
                    lastLog.status = 'FAILURE';
                    lastLog.error = error.message;
                    lastLog.duration = `${duration}s`;
                    localStorage.setItem('auraDoc_aiLogs', JSON.stringify(logs));
                }
            } catch (e) {}

            throw error;
        }
    },

    async callProxy(prompt, system, model, apiKey) {
        // Direct call to Gemini via Functions proxy
        const url = "/api/gemini-proxy";
        
        const payload = {
            model: model || "gemini-1.5-flash",
            messages: [
                { role: "system", content: system },
                { role: "user", content: prompt }
            ]
        };

        let lastError;
        for (let i = 0; i < 3; i++) {
            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: { 
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(payload)
                });

                if (response.status === 429) {
                    const delay = Math.pow(2, i) * 1000;
                    await new Promise(r => setTimeout(r, delay));
                    continue;
                }

                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error || `API returned ${response.status}`);
                }

                const data = await response.json();
                
                // Return structured response
                return {
                    content: data.candidates?.[0]?.content?.parts?.[0]?.text || '',
                    resolvedModel: data.model || model || 'gemini-1.5-flash',
                    rawUsage: data.usage || null
                };
            } catch (error) {
                lastError = error;
            }
        }
        throw lastError || new Error("Gemini Direct Proxy Service Unavailable");
    },

    async callFreeLLM(prompt, system, model, url, apiKey) {
        const targetModel = normalizeFreeLLMModelCode(model || 'auto');
        const payload = {
            model: targetModel,
            baseUrl: url,
            apiKey,
            messages: [
                { role: "system", content: system },
                { role: "user", content: prompt }
            ]
        };

        let lastError;
        for (let i = 0; i < 3; i++) {
            try {
                const response = await fetch('/api/freellm-proxy', {
                    method: "POST",
                    headers: { 
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(payload)
                });

                if (response.status === 429) {
                    const delay = Math.pow(2, i) * 1000;
                    await new Promise(r => setTimeout(r, delay));
                    continue;
                }

                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw createOperationalAIError(errData.message || errData.error || `Upstream returned status ${response.status}`, {
                        requestId: errData.requestId,
                        code: errData.code,
                        upstreamStatus: errData.upstreamStatus,
                        upstreamErrorCode: errData.upstreamErrorCode
                    });
                }

                const data = await response.json();
                if (data.success === false) {
                    throw createOperationalAIError(data.message || `Upstream returned status ${data.upstreamStatus || response.status}`, {
                        requestId: data.requestId,
                        code: data.code,
                        upstreamStatus: data.upstreamStatus,
                        upstreamErrorCode: data.upstreamErrorCode
                    });
                }
                return {
                    content: extractChatCompletionContent(data, response.status),
                    resolvedModel: data.model || targetModel || 'auto',
                    rawUsage: data.usage || null
                };
            } catch (error) {
                lastError = error;
            }
        }
        throw lastError || new Error("Free LLM Proxy Service Unavailable");
    },

    async callOpenRouter(prompt, system, model, apiKey, imageBase64 = null) {
        const url = "https://openrouter.ai/api/v1/chat/completions";
        
        let content;
        if (imageBase64) {
            content = [
                { type: "text", text: prompt },
                {
                    type: "image_url",
                    image_url: {
                        url: imageBase64.startsWith('data:') ? imageBase64 : `data:image/jpeg;base64,${imageBase64}`
                    }
                }
            ];
        } else {
            content = prompt;
        }

        const payload = {
            model: model,
            messages: [
                { role: "system", content: system },
                { role: "user", content: content }
            ]
        };

        let lastError;
        for (let i = 0; i < 3; i++) {
            try {
                const response = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${apiKey}`,
                        "HTTP-Referer": "https://auradoc.vivaura.com",
                        "X-Title": "AuraDoc Workstation"
                    },
                    body: JSON.stringify(payload)
                });

                if (response.status === 429) {
                    const delay = Math.pow(2, i) * 1000;
                    await new Promise(r => setTimeout(r, delay));
                    continue;
                }

                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error?.message || `OpenRouter returned ${response.status}`);
                }

                const data = await response.json();
                return {
                    content: data.choices?.[0]?.message?.content || '',
                    resolvedModel: data.model || model || 'openrouter/auto',
                    rawUsage: data.usage || null
                };
            } catch (error) {
                lastError = error;
            }
        }
        throw lastError || new Error("OpenRouter Service Unavailable");
    },

    async callVisionAI(imageBase64, customPrompt = "") {
        const adminState = JSON.parse(localStorage.getItem('auraDoc_adminState') || '{}');
        const apiKey = adminState.openrouterApiKey;
        const model = "google/gemini-pro-1.5-exp" || "openai/gpt-4o-mini"; 
        const system = "너는 웹 프론트엔드 개발자이자 UI/UX 디자이너이다. 제공된 이미지를 분석하여 동일한 레이아웃과 스타일을 가진 HTML과 CSS 코드를 생성하라. 오직 코드만 응답하라.";
        const prompt = customPrompt || "이 이미지의 디자인을 분석해서 AuraDoc 템플릿으로 사용할 수 있는 HTML 구조와 CSS 스타일을 생성해줘. CSS는 :root 변수를 사용하고, HTML은 <div class=\"slide\"> 내부에 내용을 구성해줘.";

        const res = await this.callOpenRouter(prompt, system, model, apiKey, imageBase64);
        return res.content;
    },

    async callOllama(prompt, url, model) {
        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                model: model,
                prompt: prompt,
                stream: false
            })
        });

        if (!response.ok) throw new Error(`Ollama returned ${response.status}`);
        const data = await response.json();
        return {
            content: data.response || '',
            resolvedModel: data.model || model || 'gemma',
            rawUsage: null
        };
    },
    getLogs() {
        try {
            return JSON.parse(localStorage.getItem('auraDoc_aiLogs') || '[]');
        } catch(e) { return []; }
    },

    async transcribeAudio(file) {
        const adminState = JSON.parse(localStorage.getItem('auraDoc_adminState') || '{}');
        const provider = adminState.aiProvider || 'gemini';

        if (provider !== 'gemini') {
            const msg = "음성 인식(STT) 기능은 현재 'Google Gemini' 모델에서만 최적화되어 지원됩니다.\n\n설정 메뉴(⚙️)에서 AI 제공자를 'Google Gemini'로 변경하신 후 다시 시도해 주세요.";
            alert(msg);
            throw new Error("Unsupported provider for STT");
        }

        const fileToBase64 = (file) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = error => reject(error);
        });

        const base64Data = await fileToBase64(file);
        const mimeType = file.type || 'audio/mpeg';

        const prompt = "이 오디오 파일의 내용을 텍스트로 정확히 받아써주세요. 화자 구분이나 타임스탬프는 제외하고 순수하게 대화 내용만 텍스트로 출력하세요.";
        
        const url = "https://auradoc-api-proxy.eric-moon.workers.dev/";
        const payload = {
            contents: [{
                parts: [
                    { inline_data: { mime_type: mimeType, data: base64Data } },
                    { text: prompt }
                ]
            }]
        };

        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error("Gemini 음성 분석 실패");
        
        const data = await response.json();
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
        
        if (!text) throw new Error("추출된 텍스트가 없습니다.");
        return text;
    },

    async searchPatentFromAPI(searchWord) {
        if (!searchWord) return "";
        try {
            const response = await fetch(`/api/patent-proxy?searchWord=${encodeURIComponent(searchWord)}`);
            if (response.ok) {
                const data = await response.json();
                const items = data.response?.body?.items || data.response?.body?.items?.item || [];
                const list = Array.isArray(items) ? (items.filter(Boolean)) : [items].filter(Boolean);
                if (list.length > 0) {
                    return list.map(item => `- 특허명: ${item.astrtCont || item.patntNm || '표준특허'}\n  출원번호: ${item.applno || ''}\n  요약: ${item.summary || item.astrtCont || '상세내용 생략'}`).join('\n\n');
                }
            }
        } catch (e) {
            console.warn("Patent RAG fetch failed:", e);
        }
        return "";
    },

    async testConnection(config) {
        const provider = config.provider;
        const requestedModel = config.requestedModel;
        const endpoint = config.endpoint;
        const apiKey = config.apiKey;

        const startTime = performance.now();
        let resultObj = {
            success: false,
            latency: 0,
            requestedModel,
            resolvedModel: requestedModel,
            requestId: 'n/a',
            message: ''
        };

        const prompt = 'Respond with OK only.';

        try {
            let res;
            if (provider === 'local') {
                res = await this.callOllamaOneShot(prompt, endpoint, requestedModel);
            } else if (provider === 'openrouter') {
                res = await this.callOpenRouterOneShot(prompt, requestedModel, apiKey);
            } else if (provider === 'freellmapi') {
                res = await this.callFreeLLMOneShot(prompt, requestedModel, endpoint, apiKey);
            } else {
                res = await this.callProxyOneShot(prompt, requestedModel, apiKey);
            }

            const duration = (performance.now() - startTime).toFixed(0);
            
            resultObj.success = true;
            resultObj.latency = duration;
            resultObj.resolvedModel = res.resolvedModel;
            resultObj.requestId = res.requestId || 'n/a';
            resultObj.message = res.content;
        } catch (error) {
            const duration = (performance.now() - startTime).toFixed(0);
            resultObj.success = false;
            resultObj.latency = duration;
            resultObj.message = error.message;
            if (error.code) resultObj.code = error.code;
            if (error.upstreamErrorCode) resultObj.upstreamErrorCode = error.upstreamErrorCode;
            if (error.requestId) resultObj.requestId = error.requestId;
        }

        // Write connection test log (redacted: no key, no prompt body, no system prompt)
        try {
            let connLogs = JSON.parse(localStorage.getItem('auraDoc_connectionLogs') || '[]');
            const logEntry = {
                timestamp: new Date().toISOString(),
                provider,
                requestedModel: resultObj.requestedModel,
                resolvedModel: resultObj.resolvedModel,
                success: resultObj.success,
                latency: `${resultObj.latency}ms`,
                requestId: resultObj.requestId,
                message: resultObj.success ? "Connection OK" : resultObj.message
            };
            connLogs.push(logEntry);
            if (connLogs.length > 50) connLogs.shift();
            localStorage.setItem('auraDoc_connectionLogs', JSON.stringify(connLogs));
        } catch (e) {}

        return resultObj;
    },

    async callOllamaOneShot(prompt, url, model) {
        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                model: model,
                prompt: prompt,
                stream: false
            })
        });

        if (!response.ok) {
            const err = new Error(`Ollama returned status ${response.status}`);
            err.isOperational = true;
            throw err;
        }
        const data = await response.json();
        return {
            content: data.response || '',
            resolvedModel: data.model || model || 'gemma'
        };
    },

    async callOpenRouterOneShot(prompt, model, apiKey) {
        const url = "https://openrouter.ai/api/v1/chat/completions";
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${apiKey}`,
                "HTTP-Referer": "https://auradoc.vivaura.com",
                "X-Title": "AuraDoc Workstation"
            },
            body: JSON.stringify({
                model: model,
                messages: [{ role: "user", content: prompt }]
            })
        });

        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            const err = new Error(errData.error?.message || `OpenRouter returned status ${response.status}`);
            err.isOperational = true;
            throw err;
        }

        const data = await response.json();
        return {
            content: data.choices?.[0]?.message?.content || '',
            resolvedModel: data.model || model || 'openrouter/auto'
        };
    },

    async callFreeLLMOneShot(prompt, model, url, apiKey) {
        const targetModel = normalizeFreeLLMModelCode(model || 'auto');
        const response = await fetch('/api/freellm-proxy', {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                model: targetModel,
                baseUrl: url,
                apiKey,
                messages: [{ role: "user", content: prompt }]
            })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false) {
            throw createOperationalAIError(data.message || `Upstream returned status ${response.status}`, {
                requestId: data.requestId,
                code: data.code,
                upstreamErrorCode: data.upstreamErrorCode
            });
        }

        return {
            content: extractChatCompletionContent(data, response.status),
            resolvedModel: data.model || targetModel || 'auto',
            requestId: data.requestId
        };
    },

    async callProxyOneShot(prompt, model, apiKey) {
        const response = await fetch("/api/gemini-proxy", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                model: model,
                messages: [{ role: "user", content: prompt }]
            })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false) {
            const err = new Error(data.error || data.message || `Gemini proxy returned status ${response.status}`);
            if (data.requestId) err.requestId = data.requestId;
            err.isOperational = true;
            throw err;
        }

        return {
            content: data.candidates?.[0]?.content?.parts?.[0]?.text || '',
            resolvedModel: data.model || model || 'gemini-1.5-flash',
            requestId: data.requestId
        };
    }
};
